import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { SponsorStatisticsSlice } from './sponsorStatistics.slice';
import {
  QCPerStatusType,
  ReadingsPerStatusType,
  UploadsPerVisitType,
  DailyQCType,
  DailyReadsType,
  UploadPerSiteType,
  StatisticsParams,
} from './sponsorStatistics.types';

export type WithoutParamsKey =
  | 'uploadsPerVisit'
  | 'qcPerStatus'
  | 'readingsPerStatus';
export type WithParamsKey = 'uploadsPerSite' | 'dailyQC' | 'dailyReads';

export type WithoutParamsDataMap = {
  uploadsPerVisit: UploadsPerVisitType[];
  qcPerStatus: QCPerStatusType[];
  readingsPerStatus: ReadingsPerStatusType[];
};

export type WithParamsDataMap = {
  uploadsPerSite: UploadPerSiteType[];
  dailyQC: DailyQCType[];
  dailyReads: DailyReadsType[];
};

export type ActionCreators<T, P = void> = {
  fetch: ActionCreatorWithPayload<
    P extends void
      ? { clinicalTrialId: string }
      : { clinicalTrialId: string; params: P },
    string
  >;
  success: ActionCreatorWithPayload<T, string>;
  error: ActionCreatorWithPayload<string, string>;
};

export type StatisticConfig<K extends string, T, P = void> = {
  apiType: string;
  key: K;
  dataType: T;
  actions: ActionCreators<T, P>;
};

export type SliceActions = typeof SponsorStatisticsSlice.actions;
export type WithoutParamsConfigType = {
  [K in WithoutParamsKey]: StatisticConfig<K, WithoutParamsDataMap[K]>;
};

export type WithParamsConfigType = {
  [K in WithParamsKey]: StatisticConfig<
    K,
    WithParamsDataMap[K],
    StatisticsParams
  >;
};

export function getActionCreator<T>(
  actions: SliceActions,
  actionName: keyof SliceActions
): ActionCreatorWithPayload<T, string> {
  return actions[actionName] as unknown as ActionCreatorWithPayload<T, string>;
}

export function createActionCreators<K extends string, T, P = void>(
  actions: SliceActions,
  key: K
): ActionCreators<T, P> {
  const fetchKey =
    `fetch${key.charAt(0).toUpperCase() + key.slice(1)}` as keyof SliceActions;
  const successKey = `${fetchKey}Success` as keyof SliceActions;
  const errorKey = `${fetchKey}Error` as keyof SliceActions;

  return {
    fetch: getActionCreator<
      P extends void
        ? { clinicalTrialId: string }
        : { clinicalTrialId: string; params: P }
    >(actions, fetchKey),
    success: getActionCreator<T>(actions, successKey),
    error: getActionCreator<string>(actions, errorKey),
  };
}

export const withoutParamsConfig: WithoutParamsConfigType = {
  uploadsPerVisit: {
    apiType: 'UPLOADS_STATUS_PER_VISIT_LIST',
    key: 'uploadsPerVisit',
    dataType: [] as UploadsPerVisitType[],
    actions: createActionCreators<'uploadsPerVisit', UploadsPerVisitType[]>(
      SponsorStatisticsSlice.actions,
      'uploadsPerVisit'
    ),
  },
  qcPerStatus: {
    apiType: 'IS_STATUS_LIST',
    key: 'qcPerStatus',
    dataType: [] as QCPerStatusType[],
    actions: createActionCreators<'qcPerStatus', QCPerStatusType[]>(
      SponsorStatisticsSlice.actions,
      'qcPerStatus'
    ),
  },
  readingsPerStatus: {
    apiType: 'READER_STATUS_LIST',
    key: 'readingsPerStatus',
    dataType: [] as ReadingsPerStatusType[],
    actions: createActionCreators<'readingsPerStatus', ReadingsPerStatusType[]>(
      SponsorStatisticsSlice.actions,
      'readingsPerStatus'
    ),
  },
};

export const withParamsConfig: WithParamsConfigType = {
  uploadsPerSite: {
    apiType: 'UPLOAD_LIST',
    key: 'uploadsPerSite',
    dataType: [] as UploadPerSiteType[],
    actions: createActionCreators<
      'uploadsPerSite',
      UploadPerSiteType[],
      StatisticsParams
    >(SponsorStatisticsSlice.actions, 'uploadsPerSite'),
  },
  dailyQC: {
    apiType: 'IS_COMPLETED_TASKS',
    key: 'dailyQC',
    dataType: [] as DailyQCType[],
    actions: createActionCreators<'dailyQC', DailyQCType[], StatisticsParams>(
      SponsorStatisticsSlice.actions,
      'dailyQC'
    ),
  },
  dailyReads: {
    apiType: 'READER_COMPLETED_TASKS',
    key: 'dailyReads',
    dataType: [] as DailyReadsType[],
    actions: createActionCreators<
      'dailyReads',
      DailyReadsType[],
      StatisticsParams
    >(SponsorStatisticsSlice.actions, 'dailyReads'),
  },
};
